/**
 * Password requirements panel
 *
 * Reports which of the four password rules the value in the new-password field
 * actually satisfies, as it is typed, plus a running "N of 4 met" count. The
 * panel is honest from the first paint: nothing is marked met until the rule is
 * met. It is display only - it never blocks submit.
 *
 * Markup contract (see app/views/users/passwords/edit.html.erb):
 *   input[data-password-strength-input]       the field being judged; bound by
 *                                             data attribute, not by id, so the
 *                                             panel is not welded to one page
 *   [data-password-strength]                  the panel
 *     [data-requirement-counter]              the "N of 4 met" readout
 *     li[data-requirement="length|case|number|special"][data-met]
 *       [data-requirement-icon="met"]         wraps the fa-solid fa-circle-check
 *       [data-requirement-icon="unmet"]       wraps the fa-regular fa-circle
 *
 * Both marks ship in the markup and the script switches which one is `hidden`.
 * Swapping `fa-*` classes does NOT work: FontAwesome's JS build rewrites each
 * `<i>` into a nested `<svg>` and strips the `fa-*` classes off the `<i>` while
 * doing it, so nothing on screen would change. The wrappers are inert markup
 * FontAwesome never touches.
 *
 * Row text colour is declarative - the ERB carries
 * `group-data-[met=true]:text-text-strong`, keyed off the `data-met` attribute
 * this script sets - so no token class is ever mutated from here.
 */

/** The panel root. */
const PANEL_SELECTOR = "[data-password-strength]";

/** The field the panel judges. */
const INPUT_SELECTOR = "[data-password-strength-input]";

/** The "N of 4 met" readout inside a panel. */
const COUNTER_SELECTOR = "[data-requirement-counter]";

/** One row per rule inside a panel. */
const ROW_SELECTOR = "li[data-requirement]";

/** The rules the panel reports on. */
export type RequirementKey = "length" | "case" | "number" | "special";

/** Which rules a given password satisfies. */
export type RequirementResults = Record<RequirementKey, boolean>;

/** The minimum length the `length` rule accepts. */
const MINIMUM_LENGTH = 8;

/** The characters the `special` rule accepts. */
const SPECIAL_CHARACTER = /[!@#$%^&*(),.?":{}|<>@$!%*?&#]/;

export interface PasswordStrengthHandle {
  /** Tear down the listener this panel registered. */
  destroy: () => void;
}

/**
 * Judge a password against the four rules the panel lists.
 *
 * @param password - the current value of the field
 * @returns one boolean per rule, keyed by its `data-requirement` value
 */
export function evaluatePassword(password: string): RequirementResults {
  return {
    length: password.length >= MINIMUM_LENGTH,
    case: /[a-z]/.test(password) && /[A-Z]/.test(password),
    number: /\d/.test(password),
    special: SPECIAL_CHARACTER.test(password),
  };
}

/**
 * Paint one row's state.
 *
 * @param row - the `li[data-requirement]` element
 * @param met - whether the row's rule is satisfied
 */
function renderRow(row: HTMLElement, met: boolean): void {
  row.setAttribute("data-met", met ? "true" : "false");
  row
    .querySelector('[data-requirement-icon="met"]')
    ?.toggleAttribute("hidden", !met);
  row
    .querySelector('[data-requirement-icon="unmet"]')
    ?.toggleAttribute("hidden", met);
}

/**
 * Mount a single panel against its field.
 *
 * The field is looked up inside the panel's own form when there is one, so a
 * page carrying more than one form binds each panel to its own field.
 *
 * @param panel - the `[data-password-strength]` element
 * @returns a handle, or `null` when the panel has no field to judge
 */
export function mountPasswordStrengthPanel(
  panel: HTMLElement,
): PasswordStrengthHandle | null {
  const scope: ParentNode = panel.closest("form") ?? document;
  const input = scope.querySelector<HTMLInputElement>(INPUT_SELECTOR);
  if (!input) return null;

  const counter = panel.querySelector<HTMLElement>(COUNTER_SELECTOR);
  const rows = Array.from(panel.querySelectorAll<HTMLElement>(ROW_SELECTOR));

  /** Re-paint every row and the counter from the field's current value. */
  const render = (): void => {
    const results = evaluatePassword(input.value);

    rows.forEach((row) => {
      const key = row.getAttribute("data-requirement") as RequirementKey | null;
      if (key && key in results) renderRow(row, results[key]);
    });

    if (counter) {
      const met = Object.values(results).filter(Boolean).length;
      counter.textContent = `${met} of ${Object.keys(results).length} met`;
    }
  };

  input.addEventListener("input", render);
  // Paint once on mount, so a value restored by the browser is reported too.
  render();

  return {
    destroy: () => input.removeEventListener("input", render),
  };
}

/**
 * Mount every requirements panel found inside a scope.
 *
 * @param scope - the element or document to search; defaults to `document`
 * @returns one handle per mounted panel - an empty array on the auth pages that
 *          carry no panel, which is all of them but the new-password page
 */
export function mountPasswordStrength(
  scope: ParentNode = document,
): PasswordStrengthHandle[] {
  return Array.from(scope.querySelectorAll<HTMLElement>(PANEL_SELECTOR))
    .map((panel) => mountPasswordStrengthPanel(panel))
    .filter((handle): handle is PasswordStrengthHandle => handle !== null);
}
